import * as Types from "../constants/ReportDashboard";
import { Types as ProfileTypes } from "../constants/actionTypes";
import {
  DEFAULT_MIN_ADHERENCE,
  DEFAULT_MAX_ADHERENCE,
  DEFAULT_REPORT_DASHBOARD_FILTERS,
} from "../utils/ReportDashboard";
import {
  DISCHARGE_FEEDBACK_COMMENTS_PAGE_SIZE,
  DISCHARGE_FEEDBACK_COMMENTS_DEFAULT_SORT_BY,
  DISCHARGE_FEEDBACK_COMMENTS_DEFAULT_SORT_DIRECTION,
} from "../utils/dischargeFeedbackComments";
import { getRtmClaimDetailsTabKey } from "../utils/rtmClaims";
import {
  PATIENT_ENGAGEMENT_PATIENT_DETAILS_PAGE_SIZE,
  PATIENT_ENGAGEMENT_PATIENT_DETAILS_DEFAULT_SORT_BY,
  PATIENT_ENGAGEMENT_PATIENT_DETAILS_DEFAULT_SORT_DIRECTION,
} from "../utils/patientEngagementEpisodes";
import {
  PROVIDER_ENGAGEMENT_PROVIDERS_PAGE_SIZE,
  PROVIDER_DETAILS_DEFAULT_SORT_BY,
  PROVIDER_DETAILS_DEFAULT_SORT_DIRECTION,
} from "../utils/providerEngagement";

const defaultSelectedFilters = { ...DEFAULT_REPORT_DASHBOARD_FILTERS };

const filterListState = {
  loading: false,
  loadingMore: false,
  error: null,
  list: [],
  pageNo: 0,
  totalPage: 0,
  totalRecords: 0,
  hasMore: false,
  searchFilter: "",
};

const rtmClaimDetailsTabState = {
  loading: false,
  loadingMore: false,
  error: null,
  list: [],
  pageNo: 0,
  totalPage: 0,
  totalRecords: 0,
  hasMore: false,
  loaded: false,
};

const normalizeRtmClaimDetailsTab = (tab) => ({
  ...rtmClaimDetailsTabState,
  ...(tab ?? {}),
  list: Array.isArray(tab?.list) ? tab.list : [],
});

const mergeFilterPageSuccess = (current, payload) => {
  const { list, pageNo, totalPage, totalRecords, hasMore, append } = payload;
  return {
    ...current,
    loading: false,
    loadingMore: false,
    error: null,
    list: append ? [...current.list, ...list] : list,
    pageNo,
    totalPage,
    totalRecords,
    hasMore,
  };
};

const markFilterListLoading = (bucket) => ({
  ...filterListState,
  ...bucket,
  list: [],
  loading: true,
  loadingMore: false,
  error: null,
});

const normalizeIdList = (ids) =>
  (Array.isArray(ids) ? ids : [])
    .map((id) => (id != null && id !== "" ? String(id) : ""))
    .filter(Boolean)
    .sort();

const idsEqual = (a, b) =>
  JSON.stringify(normalizeIdList(a)) === JSON.stringify(normalizeIdList(b));

/** Reload child dropdown lists when a parent filter selection changes */
const markDependentFiltersLoading = (filters, { customersChanged, locationsChanged } = {}) => {
  let next = filters;
  if (customersChanged) {
    next = {
      ...next,
      locations: markFilterListLoading(next.locations),
      providers: markFilterListLoading(next.providers),
    };
  }
  if (locationsChanged) {
    next = {
      ...next,
      providers: markFilterListLoading(next.providers),
    };
  }
  return next;
};

/** When parent filter changes, child dropdown lists must reload — show loading immediately */
const applyChildFilterLoadingForPatch = (filters, patch) =>
  markDependentFiltersLoading(filters, {
    customersChanged: "customerIds" in patch,
    locationsChanged: "locationIds" in patch,
  });

const filterListRequestState = (current, { append = false, filter } = {}) => ({
  ...current,
  loading: !append,
  loadingMore: !!append,
  error: null,
  list: append ? current.list : [],
  searchFilter: append ? current.searchFilter : (filter ?? ""),
});

const initialState = {
  activeSection: "dashboard",
  dataOwnerTherapistId: null,
  filtersBootstrapped: false,
  filters: {
    customers: { ...filterListState },
    locations: { ...filterListState },
    providers: { ...filterListState },
    adherenceThresholds: { lowPercentage: 50, highPercentage: 80 },
  },
  selectedFilters: { ...defaultSelectedFilters },
  appliedFilters: { ...defaultSelectedFilters },
  filtersSnapshotBeforeRtm: null,
  totalRtmClaims: { loading: false, error: null, data: null },
  providerTime: { loading: false, error: null, data: null },
  patientEngagement: { loading: false, thresholdOnly: false, error: null, data: null },
  patientEngagementThresholds: { min: null, max: null },
  adherenceTrend: { loading: false, error: null, data: null },
  patientEngagementPatientDetails: {
    loading: false,
    loadingMore: false,
    error: null,
    list: [],
    pageNo: 0,
    pageSize: PATIENT_ENGAGEMENT_PATIENT_DETAILS_PAGE_SIZE,
    totalPage: 0,
    totalRecords: 0,
    hasMore: false,
    loaded: false,
    sortBy: PATIENT_ENGAGEMENT_PATIENT_DETAILS_DEFAULT_SORT_BY,
    sortDirection: PATIENT_ENGAGEMENT_PATIENT_DETAILS_DEFAULT_SORT_DIRECTION,
  },
  rtmClaims: { loading: false, error: null, data: null },
  rtmClaimDetails: {
    eligibleOnly: true,
    // Each tab caches its own paginated rows until filters change
    eligible: { ...rtmClaimDetailsTabState },
    ineligible: { ...rtmClaimDetailsTabState },
  },
  dischargeFeedbackNps: { loading: false, error: null, data: null },
  dischargeFeedbackOutcomes: { loading: false, error: null, data: null },
  dischargeFeedbackOutcomesTrend: { loading: false, error: null, data: null },
  dischargeFeedbackNpsTrend: { loading: false, error: null, data: null },
  dischargeFeedbackComments: {
    loading: false,
    error: null,
    list: [],
    pageNo: 0,
    pageSize: DISCHARGE_FEEDBACK_COMMENTS_PAGE_SIZE,
    totalPage: 0,
    totalRecords: 0,
    sortBy: DISCHARGE_FEEDBACK_COMMENTS_DEFAULT_SORT_BY,
    sortDirection: DISCHARGE_FEEDBACK_COMMENTS_DEFAULT_SORT_DIRECTION,
  },
  providerEngagementSummary: { loading: false, error: null, data: null },
  providerEngagementTrend: { loading: false, error: null, data: null },
  providerEngagementProviders: {
    loading: false,
    loadingMore: false,
    error: null,
    list: [],
    pageNo: 0,
    pageSize: PROVIDER_ENGAGEMENT_PROVIDERS_PAGE_SIZE,
    totalPage: 0,
    totalRecords: 0,
    hasMore: false,
    sortBy: PROVIDER_DETAILS_DEFAULT_SORT_BY,
    sortDirection: PROVIDER_DETAILS_DEFAULT_SORT_DIRECTION,
  },
};

/** Clear cached report section payloads after applied filters change */
const clearSectionDataCaches = (state) => ({
  totalRtmClaims: { ...initialState.totalRtmClaims },
  providerTime: { ...initialState.providerTime },
  patientEngagement: { ...initialState.patientEngagement },
  patientEngagementThresholds: { min: null, max: null },
  adherenceTrend: { ...initialState.adherenceTrend },
  patientEngagementPatientDetails: {
    ...initialState.patientEngagementPatientDetails,
    sortBy: state.patientEngagementPatientDetails?.sortBy
      ?? PATIENT_ENGAGEMENT_PATIENT_DETAILS_DEFAULT_SORT_BY,
    sortDirection: state.patientEngagementPatientDetails?.sortDirection
      ?? PATIENT_ENGAGEMENT_PATIENT_DETAILS_DEFAULT_SORT_DIRECTION,
  },
  rtmClaims: { ...initialState.rtmClaims },
  rtmClaimDetails: {
    ...initialState.rtmClaimDetails,
    eligibleOnly: state.rtmClaimDetails?.eligibleOnly ?? true,
    eligible: { ...rtmClaimDetailsTabState },
    ineligible: { ...rtmClaimDetailsTabState },
  },
  dischargeFeedbackNps: { ...initialState.dischargeFeedbackNps },
  dischargeFeedbackOutcomes: { ...initialState.dischargeFeedbackOutcomes },
  dischargeFeedbackOutcomesTrend: { ...initialState.dischargeFeedbackOutcomesTrend },
  dischargeFeedbackNpsTrend: { ...initialState.dischargeFeedbackNpsTrend },
  dischargeFeedbackComments: {
    ...initialState.dischargeFeedbackComments,
    sortBy: state.dischargeFeedbackComments?.sortBy
      ?? DISCHARGE_FEEDBACK_COMMENTS_DEFAULT_SORT_BY,
    sortDirection: state.dischargeFeedbackComments?.sortDirection
      ?? DISCHARGE_FEEDBACK_COMMENTS_DEFAULT_SORT_DIRECTION,
  },
  providerEngagementSummary: { ...initialState.providerEngagementSummary },
  providerEngagementTrend: { ...initialState.providerEngagementTrend },
  providerEngagementProviders: {
    ...initialState.providerEngagementProviders,
    sortBy: state.providerEngagementProviders?.sortBy
      ?? PROVIDER_DETAILS_DEFAULT_SORT_BY,
    sortDirection: state.providerEngagementProviders?.sortDirection
      ?? PROVIDER_DETAILS_DEFAULT_SORT_DIRECTION,
  },
});

const clearRtmSectionCaches = (state) => ({
  rtmClaims: { ...initialState.rtmClaims },
  rtmClaimDetails: {
    ...initialState.rtmClaimDetails,
    eligibleOnly: state.rtmClaimDetails?.eligibleOnly ?? true,
    eligible: { ...rtmClaimDetailsTabState },
    ineligible: { ...rtmClaimDetailsTabState },
  },
});

const clearNonRtmSectionCaches = (state) => {
  const { rtmClaims, rtmClaimDetails, ...rest } = clearSectionDataCaches(state);
  return rest;
};

/**
 * Specific Range is split across two pickers that no longer share values, so a change
 * to one must not throw away data fetched with the other:
 * `month` only affects RTM Claims, `startDate`/`endDate` only affect the calendar pages.
 */
const clearCachesForFilterChange = (state, previous = {}, next = {}) => {
  const changedKeys = Object.keys({ ...previous, ...next }).filter((key) => (
    JSON.stringify(previous[key] ?? null) !== JSON.stringify(next[key] ?? null)
  ));

  if (!changedKeys.length) return {};

  if (changedKeys.every((key) => key === "month")) {
    return clearRtmSectionCaches(state);
  }
  if (changedKeys.every((key) => key === "startDate" || key === "endDate")) {
    return clearNonRtmSectionCaches(state);
  }
  return clearSectionDataCaches(state);
};

const normalizeFilterList = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value) && Array.isArray(value.list)) {
    return {
      loading: !!value.loading,
      loadingMore: !!value.loadingMore,
      error: value.error ?? null,
      list: value.list,
      pageNo: value.pageNo ?? 0,
      totalPage: value.totalPage ?? 0,
      totalRecords: value.totalRecords ?? value.list.length,
      hasMore: !!value.hasMore,
      searchFilter: value.searchFilter ?? "",
    };
  }
  if (Array.isArray(value)) {
    return { ...filterListState, list: value };
  }
  return { ...filterListState };
};

const normalizeSelectedFilters = (selectedFilters = {}) => {
  const customerIds = selectedFilters.customerIds
    ?? (selectedFilters.customerId != null ? [selectedFilters.customerId] : []);

  return {
    ...defaultSelectedFilters,
    ...selectedFilters,
    customerIds: Array.isArray(customerIds) ? customerIds : [],
    locationIds: Array.isArray(selectedFilters.locationIds) ? selectedFilters.locationIds : [],
    providerIds: Array.isArray(selectedFilters.providerIds) ? selectedFilters.providerIds : [],
  };
};

const normalizeState = (state) => {
  if (!state) return initialState;

  const filters = state.filters ?? {};

  return {
    ...initialState,
    ...state,
    dataOwnerTherapistId: state.dataOwnerTherapistId ?? null,
    filtersBootstrapped: !!state.filtersBootstrapped,
    filters: {
      customers: normalizeFilterList(filters.customers),
      locations: normalizeFilterList(filters.locations),
      providers: normalizeFilterList(filters.providers),
      adherenceThresholds: filters.adherenceThresholds ?? initialState.filters.adherenceThresholds,
    },
    selectedFilters: normalizeSelectedFilters(state.selectedFilters),
    appliedFilters: normalizeSelectedFilters(
      state.appliedFilters ?? state.selectedFilters
    ),
    filtersSnapshotBeforeRtm: state.filtersSnapshotBeforeRtm ?? null,
    totalRtmClaims: state.totalRtmClaims ?? initialState.totalRtmClaims,
    providerTime: state.providerTime ?? initialState.providerTime,
    patientEngagement: state.patientEngagement ?? initialState.patientEngagement,
    patientEngagementThresholds: state.patientEngagementThresholds ?? initialState.patientEngagementThresholds,
    adherenceTrend: state.adherenceTrend ?? initialState.adherenceTrend,
    patientEngagementPatientDetails: {
      ...initialState.patientEngagementPatientDetails,
      ...(state.patientEngagementPatientDetails ?? {}),
      list: Array.isArray(state.patientEngagementPatientDetails?.list)
        ? state.patientEngagementPatientDetails.list
        : [],
    },
    rtmClaims: state.rtmClaims ?? initialState.rtmClaims,
    rtmClaimDetails: {
      ...initialState.rtmClaimDetails,
      eligibleOnly: state.rtmClaimDetails?.eligibleOnly ?? true,
      eligible: normalizeRtmClaimDetailsTab(state.rtmClaimDetails?.eligible),
      ineligible: normalizeRtmClaimDetailsTab(state.rtmClaimDetails?.ineligible),
    },
    dischargeFeedbackNps: state.dischargeFeedbackNps ?? initialState.dischargeFeedbackNps,
    dischargeFeedbackOutcomes: state.dischargeFeedbackOutcomes ?? initialState.dischargeFeedbackOutcomes,
    dischargeFeedbackOutcomesTrend: state.dischargeFeedbackOutcomesTrend ?? initialState.dischargeFeedbackOutcomesTrend,
    dischargeFeedbackNpsTrend: state.dischargeFeedbackNpsTrend ?? initialState.dischargeFeedbackNpsTrend,
    dischargeFeedbackComments: {
      ...initialState.dischargeFeedbackComments,
      ...(state.dischargeFeedbackComments ?? {}),
      list: Array.isArray(state.dischargeFeedbackComments?.list)
        ? state.dischargeFeedbackComments.list
        : [],
    },
    providerEngagementSummary: state.providerEngagementSummary ?? initialState.providerEngagementSummary,
    providerEngagementTrend: state.providerEngagementTrend ?? initialState.providerEngagementTrend,
    providerEngagementProviders: {
      ...initialState.providerEngagementProviders,
      ...(state.providerEngagementProviders ?? {}),
      list: Array.isArray(state.providerEngagementProviders?.list)
        ? state.providerEngagementProviders.list
        : [],
    },
  };
};

const reportDashboard = (state = initialState, action) => {
  const s = normalizeState(state);

  switch (action.type) {
    case Types.FETCH_RD_FILTER_CUSTOMERS_REQUEST:
      return {
        ...s,
        filters: {
          ...s.filters,
          customers: filterListRequestState(s.filters.customers, action.payload),
        },
      };
    case Types.FETCH_RD_FILTER_CUSTOMERS_SUCCESS:
      return {
        ...s,
        filters: {
          ...s.filters,
          customers: mergeFilterPageSuccess(s.filters.customers, action.payload),
        },
      };
    case Types.FETCH_RD_FILTER_CUSTOMERS_FAILURE:
      return {
        ...s,
        filters: {
          ...s.filters,
          customers: {
            ...s.filters.customers,
            loading: false,
            loadingMore: false,
            error: action.payload?.message ?? action.payload,
            list: action.payload?.append ? s.filters.customers.list : [],
          },
        },
      };

    case Types.FETCH_RD_FILTER_LOCATIONS_REQUEST:
      return {
        ...s,
        filters: {
          ...s.filters,
          locations: filterListRequestState(s.filters.locations, action.payload),
        },
      };
    case Types.FETCH_RD_FILTER_LOCATIONS_SUCCESS:
      return {
        ...s,
        filters: {
          ...s.filters,
          locations: mergeFilterPageSuccess(s.filters.locations, action.payload),
        },
      };
    case Types.FETCH_RD_FILTER_LOCATIONS_FAILURE:
      return {
        ...s,
        filters: {
          ...s.filters,
          locations: {
            ...s.filters.locations,
            loading: false,
            loadingMore: false,
            error: action.payload?.message ?? action.payload,
            list: action.payload?.append ? s.filters.locations.list : [],
          },
        },
      };

    case Types.FETCH_RD_FILTER_PROVIDERS_REQUEST:
      return {
        ...s,
        filters: {
          ...s.filters,
          providers: filterListRequestState(s.filters.providers, action.payload),
        },
      };
    case Types.FETCH_RD_FILTER_PROVIDERS_SUCCESS:
      return {
        ...s,
        filters: {
          ...s.filters,
          providers: mergeFilterPageSuccess(s.filters.providers, action.payload),
        },
      };
    case Types.FETCH_RD_FILTER_PROVIDERS_FAILURE:
      return {
        ...s,
        filters: {
          ...s.filters,
          providers: {
            ...s.filters.providers,
            loading: false,
            loadingMore: false,
            error: action.payload?.message ?? action.payload,
            list: action.payload?.append ? s.filters.providers.list : [],
          },
        },
      };

    case Types.FETCH_REPORT_DASHBOARD_REQUEST:
      return {
        ...s,
        totalRtmClaims: { loading: true, error: null, data: s.totalRtmClaims.data },
      };
    case Types.FETCH_REPORT_DASHBOARD_SUCCESS:
      return {
        ...s,
        totalRtmClaims: { loading: false, error: null, data: { totalRtmClaims: action.payload.totalRtmClaims } },
      };
    case Types.FETCH_REPORT_DASHBOARD_FAILURE:
      return {
        ...s,
        totalRtmClaims: { loading: false, error: action.payload, data: null },
      };

    case Types.FETCH_PROVIDER_TIME_SUMMARY_REQUEST:
      return {
        ...s,
        providerTime: { loading: true, error: null, data: s.providerTime.data },
      };
    case Types.FETCH_PROVIDER_TIME_SUMMARY_SUCCESS:
      return {
        ...s,
        providerTime: {
          loading: false,
          error: null,
          data: { totalProviderTimeHours: action.payload.totalProviderTimeHours },
        },
      };
    case Types.FETCH_PROVIDER_TIME_SUMMARY_FAILURE:
      return {
        ...s,
        providerTime: { loading: false, error: action.payload, data: null },
      };

    case Types.FETCH_PATIENT_ENGAGEMENT_REQUEST:
      return {
        ...s,
        patientEngagement: {
          loading: true,
          thresholdOnly: !!action.payload?.thresholdOnly,
          error: null,
          data: s.patientEngagement.data,
        },
      };
    case Types.FETCH_PATIENT_ENGAGEMENT_SUCCESS:
      return {
        ...s,
        patientEngagement: {
          loading: false,
          thresholdOnly: false,
          error: null,
          data: action.payload,
        },
        patientEngagementThresholds: {
          min: action.payload.minAdherenceThreshold != null
            ? action.payload.minAdherenceThreshold
            : s.patientEngagementThresholds.min,
          max: action.payload.maxAdherenceThreshold != null
            ? action.payload.maxAdherenceThreshold
            : s.patientEngagementThresholds.max,
        },
      };
    case Types.SET_PATIENT_ENGAGEMENT_THRESHOLDS:
      return {
        ...s,
        patientEngagementThresholds: {
          ...s.patientEngagementThresholds,
          ...action.payload,
        },
      };
    case Types.FETCH_PATIENT_ENGAGEMENT_FAILURE:
      return {
        ...s,
        patientEngagement: {
          loading: false,
          thresholdOnly: false,
          error: action.payload,
          data: null,
        },
      };

    case Types.FETCH_ADHERENCE_TREND_REQUEST:
      return { ...s, adherenceTrend: { loading: true, error: null, data: s.adherenceTrend.data } };
    case Types.FETCH_ADHERENCE_TREND_SUCCESS:
      return { ...s, adherenceTrend: { loading: false, error: null, data: action.payload } };
    case Types.FETCH_ADHERENCE_TREND_FAILURE:
      return { ...s, adherenceTrend: { loading: false, error: action.payload, data: null } };

    case Types.FETCH_PE_PATIENT_DETAILS_REQUEST:
      return {
        ...s,
        patientEngagementPatientDetails: {
          ...s.patientEngagementPatientDetails,
          loading: !action.payload?.append,
          loadingMore: !!action.payload?.append,
          error: null,
          ...(action.payload?.append ? {} : { list: [] }),
        },
      };
    case Types.FETCH_PE_PATIENT_DETAILS_SUCCESS:
      return {
        ...s,
        patientEngagementPatientDetails: {
          ...s.patientEngagementPatientDetails,
          ...mergeFilterPageSuccess(s.patientEngagementPatientDetails, action.payload),
          pageSize: action.payload.pageSize
            ?? s.patientEngagementPatientDetails.pageSize
            ?? PATIENT_ENGAGEMENT_PATIENT_DETAILS_PAGE_SIZE,
          loaded: true,
        },
      };
    case Types.FETCH_PE_PATIENT_DETAILS_FAILURE:
      return {
        ...s,
        patientEngagementPatientDetails: {
          ...s.patientEngagementPatientDetails,
          loading: false,
          loadingMore: false,
          error: action.payload?.message ?? action.payload,
          list: action.payload?.append ? s.patientEngagementPatientDetails.list : [],
        },
      };

    case Types.SET_PE_PATIENT_DETAILS_SORT:
      return {
        ...s,
        patientEngagementPatientDetails: {
          ...s.patientEngagementPatientDetails,
          sortBy: action.payload.sortBy,
          sortDirection: action.payload.sortDirection,
          list: [],
          pageNo: 0,
          totalPage: 0,
          totalRecords: 0,
          hasMore: false,
          loaded: false,
          error: null,
        },
      };

    case Types.FETCH_RTM_CLAIMS_REQUEST:
      return { ...s, rtmClaims: { loading: true, error: null, data: s.rtmClaims.data } };
    case Types.FETCH_RTM_CLAIMS_SUCCESS:
      return { ...s, rtmClaims: { loading: false, error: null, data: action.payload } };
    case Types.FETCH_RTM_CLAIMS_FAILURE:
      return { ...s, rtmClaims: { loading: false, error: action.payload, data: null } };

    case Types.SET_RTM_CLAIM_DETAILS_TAB:
      return {
        ...s,
        rtmClaimDetails: { ...s.rtmClaimDetails, eligibleOnly: action.payload },
      };
    case Types.FETCH_RTM_CLAIM_DETAILS_REQUEST: {
      const tabKey = getRtmClaimDetailsTabKey(action.payload.eligibleOnly);
      return {
        ...s,
        rtmClaimDetails: {
          ...s.rtmClaimDetails,
          eligibleOnly: action.payload.eligibleOnly,
          [tabKey]: {
            ...s.rtmClaimDetails[tabKey],
            loading: !action.payload.append,
            loadingMore: !!action.payload.append,
            error: null,
            ...(action.payload.append ? {} : { list: [] }),
          },
        },
      };
    }
    case Types.FETCH_RTM_CLAIM_DETAILS_SUCCESS: {
      const tabKey = getRtmClaimDetailsTabKey(action.payload.eligibleOnly);
      return {
        ...s,
        rtmClaimDetails: {
          ...s.rtmClaimDetails,
          [tabKey]: {
            ...s.rtmClaimDetails[tabKey],
            ...mergeFilterPageSuccess(s.rtmClaimDetails[tabKey], action.payload),
            loaded: true,
          },
        },
      };
    }
    case Types.FETCH_RTM_CLAIM_DETAILS_FAILURE: {
      const tabKey = getRtmClaimDetailsTabKey(action.payload?.eligibleOnly);
      return {
        ...s,
        rtmClaimDetails: {
          ...s.rtmClaimDetails,
          [tabKey]: {
            ...s.rtmClaimDetails[tabKey],
            loading: false,
            loadingMore: false,
            error: action.payload?.message ?? action.payload,
            list: action.payload?.append ? s.rtmClaimDetails[tabKey].list : [],
          },
        },
      };
    }

    case Types.FETCH_DF_NPS_REQUEST:
      return {
        ...s,
        dischargeFeedbackNps: { loading: true, error: null, data: s.dischargeFeedbackNps.data },
      };
    case Types.FETCH_DF_NPS_SUCCESS:
      return {
        ...s,
        dischargeFeedbackNps: { loading: false, error: null, data: action.payload },
      };
    case Types.FETCH_DF_NPS_FAILURE:
      return {
        ...s,
        dischargeFeedbackNps: { loading: false, error: action.payload, data: null },
      };

    case Types.FETCH_DF_OUTCOMES_REQUEST:
      return {
        ...s,
        dischargeFeedbackOutcomes: { loading: true, error: null, data: s.dischargeFeedbackOutcomes.data },
      };
    case Types.FETCH_DF_OUTCOMES_SUCCESS:
      return {
        ...s,
        dischargeFeedbackOutcomes: { loading: false, error: null, data: action.payload },
      };
    case Types.FETCH_DF_OUTCOMES_FAILURE:
      return {
        ...s,
        dischargeFeedbackOutcomes: { loading: false, error: action.payload, data: null },
      };

    case Types.FETCH_DF_OUTCOMES_TREND_REQUEST:
      return {
        ...s,
        dischargeFeedbackOutcomesTrend: {
          loading: true,
          error: null,
          data: s.dischargeFeedbackOutcomesTrend.data,
        },
      };
    case Types.FETCH_DF_OUTCOMES_TREND_SUCCESS:
      return {
        ...s,
        dischargeFeedbackOutcomesTrend: { loading: false, error: null, data: action.payload },
      };
    case Types.FETCH_DF_OUTCOMES_TREND_FAILURE:
      return {
        ...s,
        dischargeFeedbackOutcomesTrend: { loading: false, error: action.payload, data: null },
      };

    case Types.FETCH_DF_NPS_TREND_REQUEST:
      return {
        ...s,
        dischargeFeedbackNpsTrend: { loading: true, error: null, data: s.dischargeFeedbackNpsTrend.data },
      };
    case Types.FETCH_DF_NPS_TREND_SUCCESS:
      return {
        ...s,
        dischargeFeedbackNpsTrend: { loading: false, error: null, data: action.payload },
      };
    case Types.FETCH_DF_NPS_TREND_FAILURE:
      return {
        ...s,
        dischargeFeedbackNpsTrend: { loading: false, error: action.payload, data: null },
      };

    case Types.FETCH_DF_COMMENTS_REQUEST:
      return {
        ...s,
        dischargeFeedbackComments: {
          ...s.dischargeFeedbackComments,
          loading: true,
          error: null,
        },
      };
    case Types.FETCH_DF_COMMENTS_SUCCESS:
      return {
        ...s,
        dischargeFeedbackComments: {
          ...s.dischargeFeedbackComments,
          loading: false,
          error: null,
          list: action.payload.list,
          pageNo: action.payload.pageNo,
          pageSize: action.payload.pageSize,
          totalPage: action.payload.totalPage,
          totalRecords: action.payload.totalRecords,
        },
      };
    case Types.FETCH_DF_COMMENTS_FAILURE:
      return {
        ...s,
        dischargeFeedbackComments: {
          ...s.dischargeFeedbackComments,
          loading: false,
          error: action.payload,
          list: [],
        },
      };

    case Types.FETCH_PROVIDER_ENGAGEMENT_SUMMARY_REQUEST:
      return {
        ...s,
        providerEngagementSummary: {
          loading: true,
          error: null,
          data: s.providerEngagementSummary.data,
        },
      };
    case Types.FETCH_PROVIDER_ENGAGEMENT_SUMMARY_SUCCESS:
      return {
        ...s,
        providerEngagementSummary: { loading: false, error: null, data: action.payload },
      };
    case Types.FETCH_PROVIDER_ENGAGEMENT_SUMMARY_FAILURE:
      return {
        ...s,
        providerEngagementSummary: { loading: false, error: action.payload, data: null },
      };

    case Types.FETCH_PROVIDER_ENGAGEMENT_TREND_REQUEST:
      return {
        ...s,
        providerEngagementTrend: {
          loading: true,
          error: null,
          data: s.providerEngagementTrend.data,
        },
      };
    case Types.FETCH_PROVIDER_ENGAGEMENT_TREND_SUCCESS:
      return {
        ...s,
        providerEngagementTrend: { loading: false, error: null, data: action.payload },
      };
    case Types.FETCH_PROVIDER_ENGAGEMENT_TREND_FAILURE:
      return {
        ...s,
        providerEngagementTrend: { loading: false, error: action.payload, data: null },
      };

    case Types.FETCH_PROVIDER_ENGAGEMENT_PROVIDERS_REQUEST:
      return {
        ...s,
        providerEngagementProviders: {
          ...s.providerEngagementProviders,
          loading: !action.payload?.append,
          loadingMore: !!action.payload?.append,
          error: null,
          ...(action.payload?.append ? {} : { list: [] }),
        },
      };
    case Types.FETCH_PROVIDER_ENGAGEMENT_PROVIDERS_SUCCESS:
      return {
        ...s,
        providerEngagementProviders: {
          ...s.providerEngagementProviders,
          ...mergeFilterPageSuccess(s.providerEngagementProviders, action.payload),
          pageSize: action.payload.pageSize ?? s.providerEngagementProviders.pageSize,
        },
      };
    case Types.FETCH_PROVIDER_ENGAGEMENT_PROVIDERS_FAILURE:
      return {
        ...s,
        providerEngagementProviders: {
          ...s.providerEngagementProviders,
          loading: false,
          loadingMore: false,
          error: action.payload?.message ?? action.payload,
          list: action.payload?.append ? s.providerEngagementProviders.list : [],
        },
      };

    case Types.SET_DF_COMMENTS_SORT:
      return {
        ...s,
        dischargeFeedbackComments: {
          ...s.dischargeFeedbackComments,
          sortBy: action.payload.sortBy,
          sortDirection: action.payload.sortDirection,
          list: [],
          pageNo: 0,
          totalPage: 0,
          totalRecords: 0,
          error: null,
        },
      };

    case Types.SET_PROVIDER_ENGAGEMENT_PROVIDERS_SORT:
      return {
        ...s,
        providerEngagementProviders: {
          ...s.providerEngagementProviders,
          sortBy: action.payload.sortBy,
          sortDirection: action.payload.sortDirection,
          list: [],
          pageNo: 0,
          totalPage: 0,
          totalRecords: 0,
          hasMore: false,
          error: null,
        },
      };

    case Types.SET_REPORT_DASHBOARD_ACTIVE_SECTION:
      return { ...s, activeSection: action.payload };

    case Types.SET_REPORT_DASHBOARD_FILTER: {
      const { key, value } = action.payload;
      const patch = { [key]: value };
      return {
        ...s,
        selectedFilters: { ...s.selectedFilters, [key]: value },
        filters: applyChildFilterLoadingForPatch(s.filters, patch),
      };
    }

    case Types.SET_REPORT_DASHBOARD_FILTERS_PATCH: {
      const patch = action.payload || {};
      return {
        ...s,
        selectedFilters: { ...s.selectedFilters, ...patch },
        filters: applyChildFilterLoadingForPatch(s.filters, patch),
      };
    }

    case Types.APPLY_REPORT_DASHBOARD_FILTERS: {
      const next = { ...s.selectedFilters };
      return {
        ...s,
        ...clearCachesForFilterChange(s, s.appliedFilters, next),
        selectedFilters: next,
        appliedFilters: next,
      };
    }

    case Types.DISCARD_REPORT_DASHBOARD_FILTER_DRAFT:
      return {
        ...s,
        selectedFilters: { ...s.appliedFilters },
        filters: markDependentFiltersLoading(s.filters, {
          customersChanged: !idsEqual(s.selectedFilters.customerIds, s.appliedFilters.customerIds),
          locationsChanged: !idsEqual(s.selectedFilters.locationIds, s.appliedFilters.locationIds),
        }),
      };

    case Types.COMMIT_REPORT_DASHBOARD_FILTER_PATCH: {
      // Constraint-driven updates (e.g. HEP → RTM) — draft + applied together
      const patch = action.payload || {};
      const nextSelected = { ...s.selectedFilters, ...patch };
      const nextApplied = { ...s.appliedFilters, ...patch };
      return {
        ...s,
        ...clearSectionDataCaches(s),
        selectedFilters: nextSelected,
        appliedFilters: nextApplied,
      };
    }

    case Types.RESET_REPORT_DASHBOARD_FILTERS: {
      const defaults = { ...defaultSelectedFilters };
      return {
        ...s,
        ...clearSectionDataCaches(s),
        filters: markDependentFiltersLoading(s.filters, {
          customersChanged: !idsEqual(s.selectedFilters.customerIds, defaults.customerIds),
          locationsChanged: !idsEqual(s.selectedFilters.locationIds, defaults.locationIds),
        }),
        selectedFilters: defaults,
        appliedFilters: defaults,
        filtersSnapshotBeforeRtm: null,
      };
    }

    case Types.SNAPSHOT_FILTERS_BEFORE_RTM:
      // Keep first-snapshotted values (e.g. HEP) when nesting restricted sections
      return {
        ...s,
        filtersSnapshotBeforeRtm: {
          ...action.payload,
          ...(s.filtersSnapshotBeforeRtm || {}),
        },
      };

    case Types.RESTORE_FILTERS_FROM_RTM_SNAPSHOT: {
      if (!s.filtersSnapshotBeforeRtm) return s;
      const restored = {
        ...s.appliedFilters,
        ...s.filtersSnapshotBeforeRtm,
      };
      return {
        ...s,
        ...clearSectionDataCaches(s),
        filters: markDependentFiltersLoading(s.filters, {
          customersChanged: !idsEqual(s.selectedFilters.customerIds, restored.customerIds),
          locationsChanged: !idsEqual(s.selectedFilters.locationIds, restored.locationIds),
        }),
        selectedFilters: restored,
        appliedFilters: restored,
        filtersSnapshotBeforeRtm: null,
      };
    }

    case Types.RESET_REPORT_DASHBOARD:
      return {
        ...initialState,
        dataOwnerTherapistId: action.payload?.dataOwnerTherapistId ?? null,
      };

    case Types.SET_REPORT_DASHBOARD_DATA_OWNER:
      return {
        ...s,
        dataOwnerTherapistId: action.payload ?? null,
      };

    case Types.SET_REPORT_DASHBOARD_FILTERS_BOOTSTRAPPED:
      return {
        ...s,
        filtersBootstrapped: !!action.payload,
      };

    case ProfileTypes.RESET_STORE:
    case ProfileTypes.LOGOUT:
      return initialState;

    case ProfileTypes.ADD_USER:
    case ProfileTypes.LOGIN: {
      const nextTherapistId = action.payload?.user?.id ?? null;
      if (!nextTherapistId) return s;
      if (s.dataOwnerTherapistId !== nextTherapistId) {
        return { ...initialState, dataOwnerTherapistId: nextTherapistId };
      }
      return s;
    }

    default:
      return s;
  }
};

export default reportDashboard;
